import React from "react";
import { 
  FileText, Scale, Lock, CreditCard, Users, ShieldCheck, 
  Truck, AlertCircle, Heart, Gavel, UserX, Info 
} from "lucide-react";

const TermsandConditions = () => {
  const currentDate = new Date().toLocaleDateString("en-IN", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#FFF0F3] text-gray-800 font-sans selection:bg-rose-200">
      <div className="h-2 bg-rose-500 w-full sticky top-0 z-50 shadow-md" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header Section */}
        <header className="text-center mb-16">
          <div className="inline-block px-5 py-2 rounded-full bg-rose-100 text-rose-700 text-base font-extrabold mb-6 tracking-wider uppercase shadow-sm">
            Clear & Fair
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
            Terms & <span className="text-rose-500">Conditions</span>
          </h1>
          <p className="text-xl text-gray-700 font-medium italic mb-6">
            Eveya – A brand of Farmfit Agrihealth LLP
          </p>
          <div className="flex justify-center items-center gap-6 text-base font-medium text-gray-600 flex-wrap">
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Last Updated: {currentDate}
            </span>
            <span className="text-rose-400 hidden sm:inline">•</span>
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm text-rose-600 font-bold">
              Binding Agreement
            </span>
          </div>
        </header>

        <div className="space-y-12">
          {/* 1. INTRODUCTION */}
          <Section title="1. Introduction & Agreement Acceptance" icon={<FileText size={28} className="text-rose-500" />}>
            <p>
              These Terms of Service ("Terms") form a binding agreement between <strong>“Eveya”</strong>, a brand from <strong>Farmfit Agrihealth LLP</strong> (Registered Office: Vijayawada, AP) and you, the User.
            </p>
            <div className="bg-rose-50 p-6 rounded-2xl border-l-4 border-rose-500 space-y-3">
              <p className="font-bold text-rose-700 flex items-center gap-2">
                <AlertCircle size={18} /> CRITICAL NOTICE
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                <li>By accessing www.eveya.in, you acknowledge and agree to these Terms in full.</li>
                <li><strong>Age Requirement:</strong> You must be at least 18 years old and legally competent under the Indian Contract Act, 1872.</li>
                <li><strong>Auto-Renewal:</strong> Subscriptions include automatic renewal and pre-authorized payment mandates.</li>
              </ul>
            </div>
          </Section>

          {/* 2. PRODUCTS & SUBSCRIPTIONS */}
          <Section title="2. Products, Services & Availability" icon={<Scale size={28} className="text-rose-500" />}>
            <p>Our products are menstrual hygiene solutions and <strong>NOT</strong> medical devices or medications. No medical claims are made; consult a professional for health concerns.</p>
            
            <h3 className="text-xl font-bold text-gray-800 mt-6 mb-4">Subscription Plans & Pricing</h3>
            <div className="overflow-x-auto rounded-2xl border border-rose-100 shadow-sm">
              <table className="w-full text-sm text-left border-collapse">
                <thead className="bg-rose-500 text-white font-bold">
                  <tr>
                    <th className="p-4">Plan Name</th>
                    <th className="p-4 text-center">3 Months (₹13/Pad)</th>
                    <th className="p-4 text-center">6 Months (₹11.5/Pad)</th>
                    <th className="p-4 text-center">12 Months (₹10.5/Pad)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-100 bg-white">
                  <tr>
                    <td className="p-4 font-bold text-rose-600">Starter (20/40/80 Pads)</td>
                    <td className="p-4 text-center">₹259.99</td>
                    <td className="p-4 text-center">₹459.99</td>
                    <td className="p-4 text-center">₹839.99</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-bold text-rose-600">Essential (30/60/120 Pads)</td>
                    <td className="p-4 text-center">₹389.99</td>
                    <td className="p-4 text-center">₹689.99</td>
                    <td className="p-4 text-center">₹1259.99</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-bold text-rose-600">Popular (45/90/180 Pads)</td>
                    <td className="p-4 text-center">₹584.99</td>
                    <td className="p-4 text-center">₹1034.99</td>
                    <td className="p-4 text-center">₹1889.99</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-bold text-rose-600">Premium (60/120/240 Pads)</td>
                    <td className="p-4 text-center">₹779.99</td>
                    <td className="p-4 text-center">₹1378.99</td>
                    <td className="p-4 text-center">₹2519.99</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-rose-200 mt-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2 underline decoration-rose-300">RBI Compliance for Auto-Renewal:</h4>
              <ul className="space-y-2 text-sm">
                <li>• <strong>AFA:</strong> First transaction requires OTP (Additional Factor of Authentication).</li>
                <li>• <strong>Notification:</strong> You will receive an SMS/Email reminder 48 hours before any recurring debit.</li>
                <li>• <strong>UPI:</strong> Each recurring charge requires explicit manual approval in your UPI app.</li>
              </ul>
            </div>
          </Section>

          {/* 3. SAKHI SPONSORSHIP */}
          <Section title="3. Sakhi Sponsorship Program" icon={<Heart size={28} className="text-rose-500" />}>
            <p>The Sakhi Program allows customers to sponsor products for women in need. This is a charitable initiative.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-100 flex items-start gap-3">
                <AlertCircle className="text-rose-500 shrink-0" size={20} />
                <p className="text-sm font-semibold"><strong>Non-Tax Deductible:</strong> These are NOT tax-exempt donations. No tax reduction receipts provided.</p>
              </div>
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-100 flex items-start gap-3">
                <AlertCircle className="text-rose-500 shrink-0" size={20} />
                <p className="text-sm font-semibold"><strong>Non-Refundable:</strong> All sponsorships are final. No refunds under any circumstances.</p>
              </div>
            </div>
          </Section>

          {/* 4. PRICING & PAYMENT */}
          <Section title="4. Pricing, Payment & Billing" icon={<CreditCard size={28} className="text-rose-500" />}>
            <p>All prices are in <strong>INR (Indian Rupees)</strong> and include GST. We accept Cards, Net Banking, UPI, and Digital Wallets via Zoho and PhonePe.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Refunds:</strong> Processed in 5-7 days for cards, 3-4 days for UPI.</li>
              <li><strong>International:</strong> We do NOT accept international payments or serve customers outside India.</li>
              <li><strong>Failed Renewals:</strong> We retry twice over 3 days. If both fail, your subscription order is cancelled.</li>
            </ul>
          </Section>

          {/* 5. ORDER & DELIVERY */}
          <Section title="5. Order Processing & Shipping" icon={<Truck size={28} className="text-rose-500" />}>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <span className="bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded mt-1 uppercase">Subscribers</span>
                <span><strong>First delivery is FREE.</strong> Subsequent orders in the same period incur a fixed ₹50 fee.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-gray-700 text-white text-[10px] font-bold px-1.5 py-0.5 rounded mt-1 uppercase">Standard</span>
                <span>Delivery charges vary based on weight and destination.</span>
              </li>
            </ul>
            <p className="bg-amber-50 p-4 rounded-xl text-sm border border-amber-100 mt-4">
              <strong>Damaged Products:</strong> Notify us with photos <strong>WITHIN 24 HOURS</strong> of receipt. Claims after this window will not be entertained.
            </p>
          </Section>

          {/* 8. DISCLAIMERS */}
          <Section title="8. Limitations & Disclaimers" icon={<ShieldCheck size={28} className="text-rose-500" />}>
            <p>Our liability is limited to the total amount paid by you in the 12 months preceding any claim. We are <strong>not liable</strong> for allergic reactions or skin sensitivity; you assume all risks associated with personal usage suitability.</p>
          </Section>

          {/* 10. PROHIBITED CONDUCT */}
          <Section title="10. Prohibited Conduct" icon={<UserX size={28} className="text-rose-500" />}>
            <p>We maintain a zero-tolerance policy for:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm font-medium text-gray-600">
              <div className="flex items-center gap-2">❌ Payment Fraud</div>
              <div className="flex items-center gap-2">❌ System Hacking</div>
              <div className="flex items-center gap-2">❌ Bot Scraping</div>
              <div className="flex items-center gap-2">❌ Harassment of Staff</div>
            </div>
          </Section>

          {/* 11. DISPUTE RESOLUTION */}
          <Section title="11. Dispute Resolution" icon={<Gavel size={28} className="text-rose-500" />}>
            <div className="space-y-4">
              <div className="p-5 bg-white border border-rose-200 rounded-2xl shadow-sm">
                <p className="font-bold text-gray-900 mb-2">Step 1: Informal Resolution</p>
                <p className="text-sm">Contact our Grievance Officer: <strong>support@eveya.in</strong> | +91 8121514499</p>
                <p className="text-xs text-gray-500 mt-1">SLA: 15 days for resolution.</p>
              </div>
              <p className="text-sm italic">
                All disputes are subject to the exclusive jurisdiction of courts in <strong>Vijayawada, Andhra Pradesh</strong>.
              </p>
            </div>
          </Section>

          {/* FINAL FOOTER */}
          {/* <footer className="mt-24 text-center text-gray-600 text-sm pb-12 border-t border-rose-200 pt-12">
            <p className="font-bold text-gray-900">Farmfit Agrihealth LLP</p>
            <address className="not-italic text-gray-500 mt-2">
              12-137/1, Flat No: 402, Atluri Avenue, KVR Colony 1st Road,<br />
              Enikepadu, Vijayawada, Andhra Pradesh-521108, India.
            </address>
            <p className="mt-6 uppercase tracking-[0.2em] font-black text-xs text-rose-500">
              Wellness • Clarity • Respect
            </p>
          </footer> */}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="bg-white rounded-3xl p-9 border border-rose-100 shadow-xl shadow-rose-200/40 hover:shadow-rose-300/50 transition-all duration-300">
    <div className="flex items-center gap-5 mb-7">
      {icon && (
        <div className="p-3.5 bg-gradient-to-br from-rose-50 to-rose-100 rounded-2xl shadow-sm">
          {icon}
        </div>
      )}
      <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="space-y-5 text-gray-700 leading-relaxed text-[15.5px]">
      {children}
    </div>
  </section>
);

export default TermsandConditions;